<script setup>

import {computed, onMounted, ref} from "vue";
import MfSpinnerMedium from "../../mf/spinner/medium.vue";

const props = defineProps({
    channelId: {
        type: Number,
        required: true,
    }
})

const loading = ref(false);
const memberScoreboard = ref([]);
const badgeScoreboard = ref([]);

const loadStats = async () => {
    loading.value = true
    const res = await axios.get(route('community.member.channel.statistics', props.channelId));
    memberScoreboard.value = Object.values(res.data.member_scores).sort((a, b) => b.score - a.score);
    badgeScoreboard.value = Object.values(res.data.badge_scores).sort((a, b) => b.score - a.score);
    loading.value = false
}

const averageEngagement = computed(() => {
    if (badgeScoreboard.value.length === 0) return 0;
    const totalEngagement = badgeScoreboard.value.reduce((sum, badge) => sum + parseInt(badge.engagement, 10), 0);
    return (totalEngagement / badgeScoreboard.value.length).toFixed(2);
})

const averageScore = computed(() => {
    if (badgeScoreboard.value.length === 0) return 0;
    const totalAverageScore = badgeScoreboard.value.reduce((sum, badge) => sum + parseInt(badge.average_score, 10), 0);
    return (totalAverageScore / badgeScoreboard.value.length).toFixed(2);
})

const totalScore = computed(() => {
    if (badgeScoreboard.value.length === 0) return 0;
    return badgeScoreboard.value.reduce((sum, badge) => sum + badge.score, 0);
})

onMounted(() => {
    loadStats();
})

</script>

<template>
<div>
    <div v-if="loading">
        <mf-spinner-medium class="w-12 h-6"></mf-spinner-medium>
    </div>
    <div
        v-else
        class="grid gap-12"
        :class="badgeScoreboard.length > 0 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-lg mx-auto'"
    >
        <div v-if="badgeScoreboard.length > 0">
            <ul class="divide-y">
                <li
                    class="flex justify-between space-x-4 py-1 font-bold"
                >
                    <div class="text-xs w-2/5 whitespace-nowrap overflow-ellipsis overflow-hidden">
                        {{ _mft('community:community.scoreboard.team.name') }}
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ _mft('community:community.scoreboard.team.engagement') }}
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ _mft('community:community.scoreboard.team.averageScore') }}
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ _mft('community:community.scoreboard.team.score') }}
                    </div>
                </li>
                <li
                    v-for="(badge, i) in badgeScoreboard"
                    :key="i"
                    class="flex justify-between space-x-4 py-1"
                >
                    <div class="text-xs w-2/5 whitespace-nowrap overflow-ellipsis overflow-hidden">
                        {{ badge.name }}
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ badge.engagement }}%
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ badge.average_score }}
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ badge.score }}
                    </div>
                </li>
                <li
                    class="flex justify-between space-x-4 py-1 font-bold"
                >
                    <div class="text-xs w-2/5 whitespace-nowrap overflow-ellipsis overflow-hidden">
                        Total
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ averageEngagement }}%
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ averageScore }}
                    </div>
                    <div class="text-xs w-1/5 text-right">
                        {{ totalScore }}
                    </div>
                </li>
            </ul>
        </div>
        <div>
            <ul class="divide-y">
                <li
                    class="flex justify-between space-x-4 py-1 font-bold"
                >
                    <div class="text-xs">
                        {{ _mft('community:community.scoreboard.member.name') }}
                    </div>
                    <div class="text-xs text-right">
                        {{ _mft('community:community.scoreboard.member.score') }}
                    </div>
                </li>
                <li
                    v-for="(member, i) in memberScoreboard"
                    :key="i"
                    class="flex justify-between space-x-4 py-1"
                >
                    <div class="text-xs">
                        {{ member.name }}
                    </div>
                    <div class="text-xs">
                        {{ member.score }}
                    </div>
                </li>
            </ul>
        </div>
    </div>
</div>
</template>

<style scoped>

</style>
